import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./TableBody.module.scss";
import { Dispatch, useState } from "react";
import { DotsThreeIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { SquareButton } from "@/frontend/shared/ui/Button";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { EditUserRoleModal } from "../../EditUserRoleModal/EditUserRoleModal";
import { TOrganizationExperts } from "../../../model/types/types";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { AxiosError } from "axios";
import { SetStateAction } from "jotai";
import { Tag } from "@/frontend/shared/ui/Tag";
import { formatDateMonthYear } from "@/frontend/shared/lib/formatDateMonthYear/formatDateMonthYear";
import { deleteExpert } from "../../../model/services/deleteExpert";

export function TableBody({
  data,
  triggerRefetch,
  onChangePermission
}: {
  data: TOrganizationExperts[];
  triggerRefetch: () => void;
  onChangePermission: Dispatch<SetStateAction<{ isOpen: boolean; data: any }>>;
}) {
  const [shownDropshadow, setShownDropshadow] = useState(-1);
  const [isOpenEditRoleModal, setIsOpenEditRoleModal] = useState({ id: -1, isOpen: false });
  const [isOpenRemoveUserModal, setIsOpenRemoveUserModal] = useState<{ id: number | string; isOpen: boolean }>({
    id: -1,
    isOpen: false
  });

  async function removeExpert({ id }: { id: number | string }) {
    deleteExpert({ id })
      .catch((err) => {
        const axiosError = err as AxiosError<{ message: string }>;
        const errorMessage = axiosError.response?.data?.message || "Unexpected error occurred";
        snackbarConstructor(errorMessage, "error");
      })
      .then(() => {
        triggerRefetch();
        setShownDropshadow(-1);
      });
  }

  function onRemoveExpert() {
    removeExpert({ id: isOpenRemoveUserModal.id });
    setIsOpenRemoveUserModal({ id: -1, isOpen: false });
    snackbarConstructor("Expert removed", "normal");
  }

  return (
    <div className={styles.container}>
      {data?.map((item, index) => (
        <div key={index} className={styles.row}>
          <div className={styles.labelContainer}>
            {/* {item.img ? (
              <img className={styles.image} src={item.img} alt="profile" />
            ) : (
            )} */}
            <div className={styles.imagePlaceholder}>
              {item.firstName && item.lastName && item.firstName[0] + item.lastName[0]}
            </div>
            <div className={styles.nameContainer}>
              <Typography variant="label-4" color="greyscale800">
                {item.firstName || ""} {item.lastName || ""}
              </Typography>
              <Typography variant="body-3" color="greyscale500">
                {item.email || ""}
              </Typography>
            </div>
          </div>
          <div className={styles.labelContainer}>
            <Typography variant="label-4" color="greyscale800">
              {`${item.numPortfolioCompanies} Companies`}
              {!item.pending && (
                <span className={styles.manageSpan} onClick={() => onChangePermission({ isOpen: true, data: item })}>
                  Manage
                </span>
              )}
            </Typography>
          </div>
          <div className={styles.labelContainer}>
            <Typography variant="label-4" color="greyscale800">
              {item?.pending ? (
                <Tag key={index} size="small" className={styles.whiteTag}>
                  Pending invite
                </Tag>
              ) : (
                formatDateMonthYear(item?.lastActiveAt || "") || "-"
              )}
            </Typography>
          </div>
          <div className={styles.labelContainer}>
            <Typography variant="body-2" color="greyscale600">
              {formatDateMonthYear(item.createdAt) || "-"}
            </Typography>
          </div>
          <div className={styles.actionsLabelContainer}>
            <SquareButton
              size="medium"
              variant="secondary"
              onClick={() => {
                setShownDropshadow(index);
              }}
              className={styles.threeDotsBtn}
            >
              <DotsThreeIcon className={styles.threeDotsIcon} />
            </SquareButton>
            <DropshadowMenu
              isOpen={shownDropshadow === index}
              onChange={() => setShownDropshadow(index)}
              onClose={() => setShownDropshadow(-1)}
              options={[
                !item.pending && (
                  <div
                    className={styles.dropshadowOption}
                    onClick={() => onChangePermission({ isOpen: true, data: item })}
                  >
                    <Typography variant="label-4" color="greyscale800">
                      Manage Permission
                    </Typography>
                  </div>
                ),
                <div
                  className={styles.dropshadowOption}
                  onClick={() =>
                    setIsOpenRemoveUserModal({
                      id: item.pending ? item.redisKeyId || "" : item.id || "",
                      isOpen: true
                    })
                  }
                >
                  <TrashIcon />
                  <Typography variant="label-4" color="greyscale800">
                    Remove Expert
                  </Typography>
                </div>
              ]}
              top="-10px"
              right="0px"
              dividerIndexRate={item.pending ? 0 : 1}
              className={styles.optionContainer}
              optionClassName={styles.dropshadowOption}
            />
          </div>
        </div>
      ))}
      <EditUserRoleModal
        isOpen={isOpenEditRoleModal.isOpen}
        onClose={() => setIsOpenEditRoleModal({ id: -1, isOpen: false })}
      />
      <ConfirmationModal
        isOpen={isOpenRemoveUserModal.isOpen}
        onClose={() => setIsOpenRemoveUserModal({ id: -1, isOpen: false })}
        onConfirm={() => onRemoveExpert()}
        title="Remove Expert"
        content="Are you sure you want to remove this expert?"
      />
    </div>
  );
}
